import {useState} from 'react';
import {useAuth} from '../auth';
import './LoginPage.scss';

/* ── Brand icons (inline SVG, no external deps) ── */

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  );
}

function CascadeLogomark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z" />
      <path d="M12 12L20 7.5" />
      <path d="M12 12V21" />
      <path d="M12 12L4 7.5" />
    </svg>
  );
}

/* ── Component ── */

export function LoginPage() {
  const {signInWithGoogle, signInWithGithub, signInWithEmail, signUpWithEmail} =
    useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError(null);

    const fn = mode === 'signup' ? signUpWithEmail : signInWithEmail;
    const result = await fn(email, password);
    if (result.error) {
      setError(result.error);
    }
    setBusy(false);
  };

  return (
    <div className="login">
      {/* Ambient glow orbs */}
      <div className="login__glow login__glow--1" />
      <div className="login__glow login__glow--2" />
      <div className="login__glow login__glow--3" />

      <div className="login__card">
        {/* Branding */}
        <div className="login__logo">
          <div className="login__logomark">
            <CascadeLogomark />
          </div>
          <h1 className="login__title">Cascade</h1>
        </div>
        <p className="login__subtitle">AI-powered motion design</p>

        {/* OAuth */}
        <div className="login__oauth">
          <button className="login__oauth-btn" onClick={signInWithGoogle}>
            <GoogleIcon />
            Continue with Google
          </button>
          <button className="login__oauth-btn" onClick={signInWithGithub}>
            <GitHubIcon />
            Continue with GitHub
          </button>
        </div>

        {/* Divider */}
        <div className="login__divider">
          <div className="login__divider-line" />
          <span className="login__divider-text">or</span>
          <div className="login__divider-line" />
        </div>

        {/* Email / password */}
        <form className="login__form" onSubmit={handleEmailAuth}>
          <div className="login__field">
            <input
              className="login__input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="login__field">
            <input
              className="login__input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>

          {error && (
            <div className="login__error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 4.5V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
              </svg>
              {error}
            </div>
          )}

          <button className="login__submit" type="submit" disabled={busy}>
            {busy ? (
              <span className="login__spinner" />
            ) : mode === 'signup' ? (
              'Create account'
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        {/* Toggle sign-in / sign-up */}
        <p className="login__footer">
          {mode === 'signin' ? (
            <>
              No account?{' '}
              <button
                className="login__toggle"
                onClick={() => { setMode('signup'); setError(null); }}
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                className="login__toggle"
                onClick={() => { setMode('signin'); setError(null); }}
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
